<template>
  <font-awesome-icon v-if="resolveIcon" :icon="resolveIcon" :size="resolveSize" class="d-icon" />
</template>

<script>
import { library } from "@fortawesome/fontawesome-svg-core";
import {
  faUser,
  faUserShield,
  faEdit,
  faTrash,
  faPlus,
  faSearch,
  faFilter,
  faEnvelope,
  faHome,
  faCog,
  faQuestion,
  faCheck,
  faArchive,
  faUsers,
  faCalendar,
  faBuilding,
  faMapMarker,
  faMapMarkerAlt,
  faCopy,
  faSignOutAlt,
  faChevronDown,
  faChevronUp,
  faChevronLeft,
  faChevronRight,
  faArrowUp,
  faArrowDown,
  faTimes,
  faTimesCircle,
  faBars,
  faEllipsisV,
  faDownload,
  faUpload,
  faList,
  faTable,
  faClock,
  faDollarSign,
  faExclamationTriangle,
  faUniversity,
  faFileAlt,
  faCalendarAlt,
  faExternalLinkAlt,
  faExchange,
  faExchangeAlt,
  faClone,
  faEye,
  faBan,
  faMinus,
  faStar
} from "@fortawesome/free-solid-svg-icons";

import {
  faCheckSquare,
  faTimesCircle as farTimesCircle,
  faCalendar as farCalendar,
} from "@fortawesome/free-regular-svg-icons";

import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

// Add specific icons to the library
library.add(
  // Solid icons
  faUser,
  faUserShield,
  faBuilding,
  faEdit,
  faTrash,
  faPlus,
  faSearch,
  faFilter,
  faEnvelope,
  faHome,
  faCog,
  faQuestion,
  faCheck,
  faArchive,
  faUsers,
  faCalendar,
  faMapMarker,
  faMapMarkerAlt,
  faCopy,
  faSignOutAlt,
  faChevronDown,
  faChevronUp,
  faChevronLeft,
  faChevronRight,
  faArrowUp,
  faArrowDown,
  faTimes,
  faTimesCircle,
  faBars,
  faEllipsisV,
  faDownload,
  faUpload,
  faList,
  faTable,
  faClock,
  faDollarSign,
  faExclamationTriangle,
  faUniversity,
  faFileAlt,
  faCalendarAlt,
  faExternalLinkAlt,
  faClone,
  faEye,
  faBan,
  faExchange,
  faExchangeAlt,
  faMinus,
  faStar,

  // Regular icons
  faCheckSquare,
  farTimesCircle,
  farCalendar
);

export default {
  name: "DIcon",
  components: {
    FontAwesomeIcon,
  },
  props: {
    /**
     * The icon name to display
     */
    icon: {
      type: String,
      required: true,
    },
    /**
     * The size of the icon
     * @values xs, sm, md, lg, xl
     */
    size: {
      type: String,
      default: "md",
      validator: value => ['xs', 'sm', 'md', 'lg', 'xl'].indexOf(value) > -1,
    },
    /**
     * FontAwesome icon pack
     * @values fas, far, fad
     */
    pack: {
      type: String,
      default: "fas",
      validator: value => ['fas', 'far', 'fad'].indexOf(value) > -1,
    },
  },
  computed: {
    resolveIcon() {
      // Map icon names to imported Font Awesome icons
      const iconMap = {
        // Solid icons
        user: faUser,
        edit: faEdit,
        trash: faTrash,
        plus: faPlus,
        search: faSearch,
        filter: faFilter,
        envelope: faEnvelope,
        home: faHome,
        cog: faCog,
        check: faCheck,
        archive: faArchive,
        users: faUsers,
        calendar: this.pack === "far" ? farCalendar : faCalendar,
        "map-marker": faMapMarker,
        "map-marker-alt": faMapMarkerAlt,
        copy: faCopy,
        "sign-out-alt": faSignOutAlt,
        "chevron-down": faChevronDown,
        "chevron-up": faChevronUp,
        "chevron-left": faChevronLeft,
        "chevron-right": faChevronRight,
        "arrow-up": faArrowUp,
        "arrow-down": faArrowDown,
        times: faTimes,
        "times-circle": this.pack === "far" ? farTimesCircle : faTimesCircle,
        bars: faBars,
        "ellipsis-v": faEllipsisV,
        download: faDownload,
        upload: faUpload,
        list: faList,
        table: faTable,
        clock: faClock,
        "dollar-sign": faDollarSign,
        "exclamation-triangle": faExclamationTriangle,
        "user-shield": faUserShield,
        building: faBuilding,
        question: faQuestion,
        university: faUniversity,
        "file-alt": faFileAlt,
        "calendar-alt": faCalendarAlt,
        "external-link-alt": faExternalLinkAlt,
        clone: faClone,
        eye: faEye,
        ban: faBan,
        'exchange': faExchange,
        'exchange-alt': faExchangeAlt,
        minus: faMinus,
        star: faStar,

        // Regular icons
        "check-square": faCheckSquare,
      };

      if (this.icon) {
        // First try to find the icon in our explicit iconMap
        const mappedIcon = iconMap[this.icon];
        if (mappedIcon) {
          return mappedIcon;
        }
        
        // If not found in map, try to create a dynamic icon reference
        // This is for icons loaded from the database (like category.imageUrl)
        try {
          return [this.pack, this.icon];
        } catch (error) {
          console.warn(`Icon "${this.icon}" not found in pack "${this.pack}"`);
          return faQuestion;
        }
      }

      return;
    },
    resolveSize() {
      // Map size values from d-icon to Font Awesome sizes
      const sizeMap = {
        xs: "xs",
        sm: "sm",
        md: "lg",
        lg: "xl",
        xl: "2x",
      };

      return sizeMap[this.size] || "lg";
    },
  },
};
</script>

<style scoped>
:is(.bg-primary, .bg-primary-500) > .d-icon {
  color: var(--color-primary-content);
}
</style>
